import { HorizontalDivider } from "@@/shared/dividers";
import { FlexRow } from "@@/shared/flex-containers";
import { Icon } from "@@/shared/icons/icon";
import { TextBox } from "@@/shared/text";
import { useTheme } from "@emotion/react";
import {
    faCheckCircle,
    faFileAlt,
    faPaperPlane,
} from "@fortawesome/pro-solid-svg-icons";
import type { QuoteStatus } from "@towni/common";
import { translation } from "@towni/common";
import type React from "react";

type QuoteStatusBannerProps = {
    status: QuoteStatus;
    children?: React.ReactNode;
};

const QuoteStatusBanner: React.FC<QuoteStatusBannerProps> = ({
    status,
    children,
}) => {
    const theme = useTheme();

    // Define status-specific properties
    const statusConfig = {
        DRAFT: {
            icon: faFileAlt,
            text: translation({ sv: "Utkast", en: "Draft" }),
            description: translation({
                sv: "Detta är ett utkast och har inte skickats till kunden än",
                en: "This is a draft and has not been sent to the customer yet",
            }),
            backgroundColor: theme.colors.black.light95.asString,
            textColor: theme.colors.black.light30.asString,
            iconColor: theme.colors.black.light50.asString,
            borderColor: theme.colors.black.light90.asString,
        },
        PUBLISHED: {
            icon: faPaperPlane,
            text: translation({ sv: "Skickad", en: "Published" }),
            description: translation({
                sv: "Denna offert har skickats till kunden",
                en: "This quote has been sent to the customer",
            }),
            backgroundColor: theme.colors.primary.light.light95.asString,
            textColor: theme.colors.primary.dark.asString,
            iconColor: theme.colors.primary.main.asString,
            borderColor: theme.colors.primary.light.asString,
        },
        ACCEPTED: {
            icon: faCheckCircle,
            text: translation({ sv: "Accepterad", en: "Accepted" }),
            description: translation({
                sv: "Denna offert har accepterats av kunden",
                en: "This quote has been accepted by the customer",
            }),
            backgroundColor: theme.colors.primary.light.light95.asString,
            textColor: theme.colors.primary.dark.asString,
            iconColor: theme.colors.primary.main.asString,
            borderColor: theme.colors.primary.light.asString,
        },
    };

    const config = statusConfig[status];

    return (
        <FlexRow
            padding={{ all: 12 }}
            css={{
                backgroundColor: config.backgroundColor,
                borderRadius: 8,
                border: `1px solid ${config.borderColor}`,
                marginBottom: 16,
                alignItems: "center",
            }}>
            <Icon icon={config.icon} color={config.iconColor} size={1.2} />
            <HorizontalDivider S />
            <FlexRow
                fillParentWidth
                crossAxis="center"
                css={{
                    flexDirection: "column",
                    alignItems: "flex-start",
                }}>
                <TextBox
                    text={config.text}
                    weight="700"
                    color={config.textColor}
                    size="S"
                />
                <TextBox
                    text={config.description}
                    color={config.textColor}
                    size="XS"
                />
            </FlexRow>
            {children}
        </FlexRow>
    );
};

export default QuoteStatusBanner;
